import { useState } from "react";
import { Link } from "react-router-dom";
import { Star, Trash2, Pencil, Plus, X } from "lucide-react";
import "./MySpace.css";
import { ALGORITHMS, ALGORITHM_BY_SLUG } from "../data/algorithms";
import {
  useGetFavouritesQuery,
  useToggleFavouriteMutation,
} from "../redux/slices/api/favouriteSlice";
import {
  useGetMyNotesQuery,
  useCreateNoteMutation,
  useUpdateNoteMutation,
  useDeleteNoteMutation,
} from "../redux/slices/api/noteSlice";

const labelFor = (slug) => ALGORITHM_BY_SLUG[slug]?.label || slug;
const routeFor = (slug) => ALGORITHM_BY_SLUG[slug]?.route || "/";

function MySpace() {
  const { data: favData } = useGetFavouritesQuery();
  const [toggleFavourite] = useToggleFavouriteMutation();

  const { data: notesData } = useGetMyNotesQuery();
  const [createNote] = useCreateNoteMutation();
  const [updateNote] = useUpdateNoteMutation();
  const [deleteNote] = useDeleteNoteMutation();

  const favourites = favData?.favourites ?? [];
  const notes = notesData?.notes ?? [];

  // New-note form state
  const [form, setForm] = useState({ algorithmSlug: ALGORITHMS[0].slug, title: "", content: "" });
  // Inline edit state
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", content: "" });

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    try {
      await createNote(form).unwrap();
      setForm({ algorithmSlug: form.algorithmSlug, title: "", content: "" });
    } catch (err) {
      // no-op; cache stays consistent
    }
  };

  const startEdit = (note) => {
    setEditingId(note._id);
    setEditForm({ title: note.title, content: note.content || "" });
  };

  const saveEdit = async (id) => {
    try {
      await updateNote({ id, ...editForm }).unwrap();
      setEditingId(null);
    } catch (err) {
      // no-op
    }
  };

  // Group notes by algorithm slug for display
  const notesBySlug = notes.reduce((acc, n) => {
    (acc[n.algorithmSlug] = acc[n.algorithmSlug] || []).push(n);
    return acc;
  }, {});

  return (
    <div className="myspace-page">
      <div className="myspace-inner">
        <h1 className="myspace-title">My Space</h1>

        {/* ── Favourites ── */}
        <section className="myspace-section">
          <h2 className="myspace-heading">
            <Star size={18} className="heading-icon" /> Favourite Algorithms
          </h2>

          {favourites.length === 0 ? (
            <p className="myspace-empty">
              No favourites yet. Tap the ☆ next to an algorithm in the sidebar to save it here.
            </p>
          ) : (
            <div className="fav-grid">
              {favourites.map((f) => (
                <div className="fav-card" key={f._id || f.algorithmSlug}>
                  <Link to={routeFor(f.algorithmSlug)} className="fav-link">
                    {labelFor(f.algorithmSlug)}
                  </Link>
                  <button
                    className="fav-remove"
                    title="Remove from favourites"
                    onClick={() => toggleFavourite(f.algorithmSlug)}
                  >
                    <Star size={16} fill="#ffd700" color="#ffd700" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* ── Notes ── */}
        <section className="myspace-section">
          <h2 className="myspace-heading">
            <Pencil size={18} className="heading-icon" /> My Notes
          </h2>

          <form className="note-create" onSubmit={handleCreate}>
            <div className="note-create-row">
              <select
                className="note-select"
                value={form.algorithmSlug}
                onChange={(e) => setForm({ ...form, algorithmSlug: e.target.value })}
              >
                {ALGORITHMS.map((a) => (
                  <option key={a.slug} value={a.slug}>
                    {a.label}
                  </option>
                ))}
              </select>
              <input
                className="note-input"
                type="text"
                placeholder="Note title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
            </div>
            <textarea
              className="note-textarea"
              placeholder="Write your note..."
              rows={3}
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
            />
            <button type="submit" className="note-add-btn">
              <Plus size={16} /> Add Note
            </button>
          </form>

          {notes.length === 0 ? (
            <p className="myspace-empty">No notes yet. Create one above.</p>
          ) : (
            Object.keys(notesBySlug).map((slug) => (
              <div className="note-group" key={slug}>
                <h3 className="note-group-title">
                  <Link to={routeFor(slug)}>{labelFor(slug)}</Link>
                </h3>
                <div className="note-list">
                  {notesBySlug[slug].map((note) => (
                    <div className="note-card" key={note._id}>
                      {editingId === note._id ? (
                        <>
                          <input
                            className="note-input"
                            value={editForm.title}
                            onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                          />
                          <textarea
                            className="note-textarea"
                            rows={3}
                            value={editForm.content}
                            onChange={(e) => setEditForm({ ...editForm, content: e.target.value })}
                          />
                          <div className="note-actions">
                            <button className="note-save" onClick={() => saveEdit(note._id)}>
                              Save
                            </button>
                            <button className="note-cancel" onClick={() => setEditingId(null)}>
                              <X size={14} /> Cancel
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="note-head">
                            <h4 className="note-title">{note.title}</h4>
                            <div className="note-icon-actions">
                              <button className="note-icon-btn" title="Edit" onClick={() => startEdit(note)}>
                                <Pencil size={15} />
                              </button>
                              <button
                                className="note-icon-btn danger"
                                title="Delete"
                                onClick={() => deleteNote(note._id)}
                              >
                                <Trash2 size={15} />
                              </button>
                            </div>
                          </div>
                          {note.content && <p className="note-content">{note.content}</p>}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </section>
      </div>
    </div>
  );
}

export default MySpace;
